import * as RadixPopover from '@radix-ui/react-popover';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  memo,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WorkspaceAgentGuidedVariantScaleOrCropBrowse } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantScaleOrCropBrowse';
import { WorkspaceAgentGuidedVariantScaleOrCropSearchResults } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantScaleOrCropSearchResults';
import { WorkspaceAgentGuidedVariantSizeCombobox } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeCombobox';
import {
  expandedWorkspaceAgentScaleOrCropGroups,
  matchWorkspaceAgentScaleOrCropEnter,
  searchWorkspaceAgentScaleOrCrop,
  toggleWorkspaceAgentScaleOrCropSet,
  toggleWorkspaceAgentScaleOrCropSize,
  WORKSPACE_AGENT_SCALE_OR_CROP_UPSCALES,
  WORKSPACE_AGENT_SCALE_OR_CROP_WEB_DOWNSCALES,
  type WorkspaceAgentScaleConstraintSize,
  type WorkspaceAgentScaleOrCropInputMode,
  type WorkspaceAgentScaleOrCropSize,
  workspaceAgentScaleOrCropSizeKey,
  workspaceAgentScaleOrCropUpscaleChannelId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentScaleOrCrop';
import {
  WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES,
  WORKSPACE_AGENT_SMART_RESIZE_SHAPES,
  type WorkspaceAgentSmartResizeSavedSize,
  type WorkspaceAgentSmartResizeSizeSet,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

type PopoverDismissEvent = {
  detail: { originalEvent: Event };
  preventDefault: () => void;
};

export type WorkspaceAgentGuidedVariantScaleOrCropFieldProps = {
  isSavedCatalogLoading: boolean;
  onSizesChange: (sizes: WorkspaceAgentScaleOrCropSize[]) => void;
  savedSets: readonly WorkspaceAgentSmartResizeSizeSet[];
  savedSizes: readonly WorkspaceAgentSmartResizeSavedSize[];
  sizes: readonly WorkspaceAgentScaleOrCropSize[];
};

export const WorkspaceAgentGuidedVariantScaleOrCropField = memo(
  ({
    isSavedCatalogLoading,
    onSizesChange,
    savedSets,
    savedSizes,
    sizes,
  }: WorkspaceAgentGuidedVariantScaleOrCropFieldProps) => {
    const listId = useId();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [inputMode, setInputMode] = useState<WorkspaceAgentScaleOrCropInputMode>('search');
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const openMenu = useCallback(() => {
      if (!isOpenRef.current) {
        setIsOpen(true);
      }
    }, []);

    const handleOpenChange = useCallback((open: boolean) => {
      if (!open) {
        setIsOpen(false);
      }
    }, []);

    const handleOpenAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
    }, []);

    const handleCloseAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
    }, []);

    const resetInputMode = useCallback(() => {
      setInputMode('search');
      setQuery('');
    }, []);

    const dismissSearchOrMenu = useCallback(() => {
      if (query.trim()) {
        setQuery('');
        setInputMode('search');
        return;
      }

      setInputMode('search');
      setIsOpen(false);
    }, [query]);

    const handleEscapeKeyDown = useCallback(
      (event: Parameters<NonNullable<RadixPopover.PopoverContentProps['onEscapeKeyDown']>>[0]) => {
        event.preventDefault();
        dismissSearchOrMenu();
      },
      [dismissSearchOrMenu],
    );

    const dismissWhenInsideField = useCallback((event: PopoverDismissEvent) => {
      const target = event.detail.originalEvent.target;

      if (target instanceof Node && rootRef.current?.contains(target)) {
        event.preventDefault();
      }
    }, []);

    // Focusing the field opens the menu. That focus is outside the portaled content, so without this the
    // opening click closes the menu and the click opens it again.
    const handleFocusOutside = useCallback(
      (event: Parameters<NonNullable<RadixPopover.PopoverContentProps['onFocusOutside']>>[0]) => {
        dismissWhenInsideField(event);
      },
      [dismissWhenInsideField],
    );

    // A press on the field stays in the combobox. A press anywhere else only closes the menu.
    const handlePointerDownOutside = useCallback(
      (event: Parameters<NonNullable<RadixPopover.PopoverContentProps['onPointerDownOutside']>>[0]) => {
        dismissWhenInsideField(event);
      },
      [dismissWhenInsideField],
    );

    const handleQueryChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setQuery(event.currentTarget.value);
        openMenu();
      },
      [openMenu],
    );

    const handleMenuToggle = useCallback(() => {
      if (isOpen) {
        setIsOpen(false);
        return;
      }

      openMenu();
    }, [isOpen, openMenu]);

    const beginInputMode = useCallback(
      (mode: WorkspaceAgentScaleOrCropInputMode) => {
        setInputMode(mode);
        setQuery('');
        inputRef.current?.focus();
        openMenu();
      },
      [openMenu],
    );

    const [expandedGroups, setExpandedGroups] = useState(expandedWorkspaceAgentScaleOrCropGroups);
    const isAtCap = sizes.length >= WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES;
    const normalizedQuery = query.trim();
    const search = searchWorkspaceAgentScaleOrCrop({
      inputMode,
      query: normalizedQuery,
      savedSets,
      savedSizes,
    });
    const hasSearch = normalizedQuery.length > 0;
    const hasSearchResults =
      search.typed !== null ||
      search.crop.length > 0 ||
      search.sets.length > 0 ||
      search.savedSizes.length > 0 ||
      search.upscale.length > 0 ||
      search.downscale.length > 0;

    const handleToggleSize = useCallback(
      (size: WorkspaceAgentScaleOrCropSize) => {
        onSizesChange(toggleWorkspaceAgentScaleOrCropSize(sizes, size).sizes);
        resetInputMode();
      },
      [onSizesChange, resetInputMode, sizes],
    );

    const handleToggleSet = useCallback(
      (sizeSet: WorkspaceAgentSmartResizeSizeSet) => {
        onSizesChange(toggleWorkspaceAgentScaleOrCropSet(sizes, sizeSet));
        resetInputMode();
      },
      [onSizesChange, resetInputMode, sizes],
    );

    const handleToggleGroup = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      const groupId = event.currentTarget.dataset.groupId;

      if (!groupId) {
        return;
      }

      setExpandedGroups((current) => {
        const next = new Set(current);

        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }

        return next;
      });
    }, []);

    const handleExactSize = useCallback(() => {
      beginInputMode('exact');
    }, [beginInputMode]);

    const handleFocusConstraint = useCallback(
      (constraint: WorkspaceAgentScaleConstraintSize['by']) => {
        beginInputMode(constraint);
      },
      [beginInputMode],
    );

    const handleSizeClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const sizeId = event.currentTarget.dataset.sizeId;

        if (!sizeId) {
          return;
        }

        const shape = WORKSPACE_AGENT_SMART_RESIZE_SHAPES.find((item) => item.id === sizeId);

        if (shape) {
          handleToggleSize(shape.size);
          return;
        }

        const saved = savedSizes.find((item) => item.id === sizeId);

        if (saved) {
          handleToggleSize(saved.size);
          return;
        }

        const upscale = WORKSPACE_AGENT_SCALE_OR_CROP_UPSCALES.find(
          (item) => workspaceAgentScaleOrCropUpscaleChannelId(item.factor) === sizeId,
        );

        if (upscale) {
          handleToggleSize(upscale);
          return;
        }

        const downscale = WORKSPACE_AGENT_SCALE_OR_CROP_WEB_DOWNSCALES.find((item) => item.id === sizeId);

        if (downscale) {
          handleToggleSize(downscale);
        }
      },
      [handleToggleSize, savedSizes],
    );

    const handleSetClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const setId = event.currentTarget.dataset.setId;
        const sizeSet = savedSets.find((item) => item.id === setId);

        if (sizeSet) {
          handleToggleSet(sizeSet);
        }
      },
      [handleToggleSet, savedSets],
    );

    const handleRemovePill = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const sizeKey = event.currentTarget.dataset.sizeKey;
        const size = sizes.find((item) => workspaceAgentScaleOrCropSizeKey(item) === sizeKey);

        if (size) {
          handleToggleSize(size);
        }
      },
      [handleToggleSize, sizes],
    );

    const handleAddTyped = useCallback(() => {
      if (search.typed) {
        handleToggleSize(search.typed);
      }
    }, [handleToggleSize, search.typed]);

    const handleInputKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          dismissSearchOrMenu();
          return;
        }

        if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
          return;
        }

        event.preventDefault();

        const match = matchWorkspaceAgentScaleOrCropEnter({ inputMode, query, search });

        if (!match) {
          return;
        }

        if (match.kind === 'set') {
          handleToggleSet(match.sizeSet);
          return;
        }

        handleToggleSize(match.size);
      },
      [dismissSearchOrMenu, handleToggleSet, handleToggleSize, inputMode, query, search],
    );

    const placeholder = useMemo(() => {
      if (inputMode === 'exact') {
        return 'Type W × H, e.g. 1080 × 1350';
      }

      if (inputMode === 'width') {
        return 'Type a width in px, e.g. 1080';
      }

      if (inputMode === 'height') {
        return 'Type a height in px, e.g. 1080';
      }

      if (inputMode === 'long-side') {
        return 'Type the long side in px, e.g. 2048';
      }

      if (sizes.length > 0) {
        return isAtCap ? '' : 'Add a size';
      }

      return 'Pick or write a size';
    }, [isAtCap, inputMode, sizes.length]);

    return (
      <RadixPopover.Root onOpenChange={handleOpenChange} open={isOpen}>
        <RadixPopover.Anchor asChild>
          <div className="relative" ref={rootRef}>
            <WorkspaceAgentGuidedVariantSizeCombobox
              inputRef={inputRef}
              isOpen={isOpen}
              listId={listId}
              onInputKeyDown={handleInputKeyDown}
              onMenuToggle={handleMenuToggle}
              onOpen={openMenu}
              onQueryChange={handleQueryChange}
              onRemovePill={handleRemovePill}
              placeholder={placeholder}
              query={query}
              sizes={sizes}
            />
          </div>
        </RadixPopover.Anchor>
        {isOpen ? (
          <RadixPopover.Portal>
            <RadixPopover.Content
              align="start"
              className="z-portal flex max-h-[min(24rem,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-8 border border-gray-5 bg-gray-1 shadow-popover outline-none"
              collisionPadding={8}
              data-preserves-selection
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_MENU"
              onCloseAutoFocus={handleCloseAutoFocus}
              onEscapeKeyDown={handleEscapeKeyDown}
              onFocusOutside={handleFocusOutside}
              onOpenAutoFocus={handleOpenAutoFocus}
              onPointerDownOutside={handlePointerDownOutside}
              side="bottom"
              sideOffset={4}
            >
              {isAtCap ? (
                <p className="px-2 pt-3 pb-1.5 text-12 text-gray-9">
                  Up to {WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES} sizes. Remove one to add another.
                </p>
              ) : null}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-2" id={listId}>
                {hasSearch ? (
                  <WorkspaceAgentGuidedVariantScaleOrCropSearchResults
                    hasSearchResults={hasSearchResults}
                    onAddTyped={handleAddTyped}
                    onSelectSet={handleSetClick}
                    onSelectSize={handleSizeClick}
                    search={search}
                    sizes={sizes}
                  />
                ) : (
                  <WorkspaceAgentGuidedVariantScaleOrCropBrowse
                    expandedGroups={expandedGroups}
                    isExactMode={inputMode === 'exact'}
                    isSavedCatalogLoading={isSavedCatalogLoading}
                    onExactSize={handleExactSize}
                    onFocusConstraint={handleFocusConstraint}
                    onSelectSet={handleSetClick}
                    onSelectSize={handleSizeClick}
                    onToggleGroup={handleToggleGroup}
                    savedSets={savedSets}
                    savedSizes={savedSizes}
                    sizes={sizes}
                  />
                )}
              </div>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        ) : null}
      </RadixPopover.Root>
    );
  },
);

WorkspaceAgentGuidedVariantScaleOrCropField.displayName = 'WorkspaceAgentGuidedVariantScaleOrCropField';
